import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  PiggyBank, TrendingUp, TrendingDown, Plus, Trash2, GripVertical, Settings2, Save,
} from "lucide-react";

const MONTH_NOW = () => new Date().toISOString().slice(0, 7);
const CURRENCY = (n) => `€${(Number(n) || 0).toFixed(2)}`;

export default function BilancioPanel() {
  const [month, setMonth] = useState(MONTH_NOW());
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [byMacro, setByMacro] = useState({ income: {}, expense: {}, tax: {} });
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        api.get("/admin/finance/categories"),
        api.get(`/admin/finance/balance-by-macro${month ? `?month=${month}` : ""}`),
      ]);
      setCategories({ income: c.data.income || [], expense: c.data.expense || [] });
      setByMacro(b.data.by_macro || { income: {}, expense: {}, tax: {} });
    } catch {
      toast.error("Errore caricamento bilancio");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge defaults with actual aggregated values
  const incomeRows = useMemo(
    () => categories.income.map((c) => ({
      ...c,
      total: byMacro.income?.[c.id]?.total || 0,
      count: byMacro.income?.[c.id]?.count || 0,
    })),
    [categories.income, byMacro],
  );
  const expenseRows = useMemo(
    () => categories.expense.map((c) => ({
      ...c,
      total: byMacro.expense?.[c.id]?.total || 0,
      count: byMacro.expense?.[c.id]?.count || 0,
    })),
    [categories.expense, byMacro],
  );

  const totIncome = incomeRows.reduce((s, r) => s + r.total, 0);
  const totExpense = expenseRows.reduce((s, r) => s + r.total, 0);
  const totTax = Object.values(byMacro.tax || {}).reduce((s, v) => s + (v?.total || 0), 0);
  const net = totIncome - totExpense - totTax;

  return (
    <div className="space-y-5" data-testid="bilancio-panel">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <PiggyBank className="w-5 h-5" /> Bilancio dettagliato
          </h3>
          <p className="text-xs text-[#635F59] mt-1">
            Aggregato delle entrate e uscite per macro-categoria. Modifica le voci dal pulsante "Gestisci voci" — saranno usate dalla scheda Entrate/Uscite del Gestionale.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#635F59]">Mese</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 w-40"
            data-testid="bilancio-month"
          />
          <Button variant="outline" onClick={() => setMonth("")} data-testid="bilancio-all">
            Storico totale
          </Button>
          <Button onClick={() => setEditOpen(true)} data-testid="bilancio-edit-cats" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Settings2 className="w-4 h-4 mr-1" />Gestisci voci
          </Button>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard color="emerald" icon={TrendingUp} label="Entrate" value={CURRENCY(totIncome)} />
        <SummaryCard color="rose" icon={TrendingDown} label="Uscite" value={CURRENCY(totExpense)} />
        <SummaryCard color="amber" icon={TrendingDown} label="Tasse/Ritenute" value={CURRENCY(totTax)} />
        <SummaryCard color="sky" icon={PiggyBank} label="Saldo netto" value={CURRENCY(net)} highlight />
      </div>

      {/* DETAIL TABLES */}
      {loading ? (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl p-8 text-center text-[#635F59]">Caricamento…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <MacroTable kind="Entrate" rows={incomeRows} accent="emerald" total={totIncome} testId="bilancio-income" />
          <MacroTable kind="Uscite" rows={expenseRows} accent="rose" total={totExpense} testId="bilancio-expense" />
        </div>
      )}

      {/* EDIT CATEGORIES DIALOG */}
      <CategoriesEditor
        open={editOpen}
        onOpenChange={setEditOpen}
        categories={categories}
        onSaved={() => { setEditOpen(false); refresh(); }}
      />
    </div>
  );
}

// ---------- Pieces ----------
const SummaryCard = ({ icon: Icon, label, value, color, highlight }) => {
  const palette = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
  }[color];
  return (
    <div className={`rounded-xl border p-4 ${palette} ${highlight ? "ring-2 ring-offset-1 ring-current/30" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
};

const MacroTable = ({ kind, rows, accent, total, testId }) => {
  const headBg = accent === "emerald" ? "bg-emerald-700" : "bg-rose-700";
  return (
    <div className="border border-[#E8E2D9] rounded-xl overflow-hidden bg-white" data-testid={testId}>
      <div className={`${headBg} text-white px-4 py-2 flex items-center justify-between`}>
        <span className="font-semibold">{kind}</span>
        <span className="font-display text-lg">{CURRENCY(total)}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#FDFBF7] text-[#635F59] text-xs uppercase">
          <tr>
            <th className="text-left py-2 px-3">Voce</th>
            <th className="text-right py-2 px-3">N°</th>
            <th className="text-right py-2 px-3">Totale €</th>
            <th className="text-right py-2 px-3 w-16">%</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E8E2D9]">
          {rows.map((r) => {
            const pct = total > 0 ? (r.total / total) * 100 : 0;
            return (
              <tr key={r.id} data-testid={`${testId}-row-${r.id}`}>
                <td className="py-2 px-3 font-medium text-[#1E3A5F]">{r.label}</td>
                <td className="py-2 px-3 text-right text-xs text-[#635F59]">{r.count || "—"}</td>
                <td className="py-2 px-3 text-right font-semibold">{CURRENCY(r.total)}</td>
                <td className="py-2 px-3 text-right text-xs text-[#635F59]">{pct.toFixed(0)}%</td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="text-center text-sm text-[#635F59] py-6">Nessuna voce configurata.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Dialog per modifica macro-categorie (add/rename/remove + reset default)
const CategoriesEditor = ({ open, onOpenChange, categories, onSaved }) => {
  const [income, setIncome] = useState([]);
  const [expense, setExpense] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setIncome(categories.income.map((c) => ({ ...c })));
      setExpense(categories.expense.map((c) => ({ ...c })));
    }
  }, [open, categories]);

  const addRow = (kind) => {
    const id = `custom_${Date.now()}`;
    if (kind === "income") setIncome([...income, { id, label: "Nuova voce" }]);
    else setExpense([...expense, { id, label: "Nuova voce" }]);
  };
  const removeRow = (kind, id) => {
    if (kind === "income") setIncome(income.filter((c) => c.id !== id));
    else setExpense(expense.filter((c) => c.id !== id));
  };
  const updateLabel = (kind, id, label) => {
    const fn = (arr) => arr.map((c) => (c.id === id ? { ...c, label } : c));
    if (kind === "income") setIncome(fn(income));
    else setExpense(fn(expense));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/finance/categories", { income, expense });
      toast.success("Voci salvate");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bilancio-edit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" /> Gestione macro-voci bilancio</DialogTitle></DialogHeader>
        <p className="text-xs text-[#635F59]">
          Le voci sono usate quando registri Entrate/Uscite nel Gestionale e quando il sistema importa dati da Booking/Airbnb. Modifica i nomi, aggiungi nuove voci personalizzate, oppure rimuovi quelle che non usi.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <Section title="Entrate" rows={income} kind="income" onAdd={addRow} onRemove={removeRow} onChange={updateLabel} />
          <Section title="Uscite" rows={expense} kind="expense" onAdd={addRow} onRemove={removeRow} onChange={updateLabel} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="bilancio-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Save className="w-4 h-4 mr-1" />{busy ? "Salvataggio…" : "Salva tutte le voci"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, rows, kind, onAdd, onRemove, onChange }) => (
  <div className="border border-[#E8E2D9] rounded-lg p-3">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-semibold text-[#1E3A5F]">{title}</h4>
      <Button size="sm" variant="outline" onClick={() => onAdd(kind)} data-testid={`bilancio-add-${kind}`}>
        <Plus className="w-3.5 h-3.5 mr-1" />Aggiungi
      </Button>
    </div>
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2" data-testid={`bilancio-cat-${r.id}`}>
          <GripVertical className="w-3.5 h-3.5 text-[#E8E2D9] shrink-0" />
          <Input
            value={r.label}
            onChange={(e) => onChange(kind, r.id, e.target.value)}
            className="h-8 text-sm"
          />
          <Button size="icon" variant="ghost" className="text-rose-600 h-8 w-8 shrink-0" onClick={() => onRemove(kind, r.id)} data-testid={`bilancio-del-${r.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-[#635F59] italic">Nessuna voce — aggiungine almeno una.</p>}
    </div>
  </div>
);
